import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, tokenStorage, bootstrapAuth } from '../services/api';
import { clearCompanyTaxSettingsCache } from '../services/companyTaxSettings';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      const restored = await bootstrapAuth();   // silent /auth/refresh via cookie
      if (cancelled) return;
      if (!restored) {
        // No valid session — drop any stale `user` seeded from localStorage so we
        // never present a non-null user paired with a null token.
        setUser(null);
        localStorage.removeItem('user');
        setLoading(false);
        return;
      }
      setToken(restored);
      try {
        const response = await authAPI.getCurrentUser();
        const raw = response.data.data;
        const userData = { ...raw, permissions: raw.permissions ?? [] };
        if (!cancelled) { setUser(userData); localStorage.setItem('user', JSON.stringify(userData)); }
      } catch (error) {
        if (!cancelled) { console.error('Auth check failed:', error?.message); logout(); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Re-fetch the current user (role + effective permissions) and update state.
   * Used to pick up RBAC changes made by an admin without a manual re-login.
   * Transient failures are swallowed so a brief network blip never logs the
   * user out — the initial-load effect above still owns hard-failure logout.
   */
  const refreshUser = useCallback(async () => {
    if (!tokenStorage.getAccessToken()) return null;
    try {
      const response = await authAPI.getCurrentUser();
      const raw = response.data.data;
      const userData = { ...raw, permissions: raw.permissions ?? [] };
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    } catch (e) {
      console.warn('Session refresh failed:', e?.message);
      return null;
    }
  }, []);

  // Re-pull permissions/role when the tab regains focus, so an admin's change
  // to this user's role or per-user overrides reflects without a manual re-login.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshUser();
    };
    window.addEventListener('focus', refreshUser);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refreshUser);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshUser]);

  // Keep React state in step with the access token the HTTP layer rotates on a
  // silent refresh (it writes straight to localStorage). Without this the in-memory
  // `token` would stay frozen at the login value while the real one rotated — the
  // root cause of live sync dying after the first 30-minute expiry.
  useEffect(() => tokenStorage.onAccessTokenChange((newToken) => {
    setToken((prev) => (prev === newToken ? prev : newToken));
  }), []);

  /**
   * Accepts the new AuthResponse shape:
   *   { accessToken, expiresIn, user: { fullName, email, role, permissions }, message }
   *   (refreshToken is no longer in the body — it arrives as an HttpOnly cookie)
   */
  const login = (authResponse) => {
    const { accessToken, user: u } = authResponse;
    clearCompanyTaxSettingsCache(); // fresh session must re-fetch company tax settings
    tokenStorage.setTokens({ accessToken });
    setToken(accessToken);
    // The signin response now embeds the effective `role` + `permissions` (resolved
    // server-side via the same path as /auth/me), so the role-based dashboard and
    // permission gates render instantly — no follow-up /auth/me round-trip needed.
    const userData = { ...u, permissions: u.permissions ?? [] };
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  // Stable identity (useCallback) so consumers like RealtimeProvider can depend on
  // it without tearing down and rebuilding the WebSocket on every re-render.
  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (e) {
      console.warn('Logout API failed; clearing local session:', e?.message);
    }
    clearCompanyTaxSettingsCache(); // drop cached company state code so the next session re-fetches
    tokenStorage.clearTokens();
    setToken(null);
    setUser(null);
  }, []);

  const isAuthenticated = !!token && !!user;

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, isAuthenticated, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
